import { useEffect, useRef, useState } from "react";
import { Play, Pause, X, ExternalLink, Disc3 } from "lucide-react";

const PREVIEW_SECONDS = 60;

export const PlayerBar = ({ track, onClose }) => {
  const [elapsed, setElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    setElapsed(0);
    setPaused(false);
  }, [track?.cat]);

  useEffect(() => {
    if (!track || paused) return;
    timer.current = setInterval(() => {
      setElapsed((e) => {
        if (e >= PREVIEW_SECONDS) { clearInterval(timer.current); return PREVIEW_SECONDS; }
        return e + 1;
      });
    }, 1000);
    return () => clearInterval(timer.current);
  }, [track, paused]);

  if (!track) return null;

  const done = elapsed >= PREVIEW_SECONDS;
  const fmt = (s) => `0${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div data-testid="sticky-audio-player" className="fixed bottom-0 inset-x-0 z-50 glass-dark border-t border-[#00f0ff]/30">
      <div className="h-0.5 bg-white/10">
        <div className="h-full bg-gradient-to-r from-[#00f0ff] via-[#ff0055] to-[#00ff66] transition-all duration-1000 ease-linear" style={{ width: `${(elapsed / PREVIEW_SECONDS) * 100}%` }} />
      </div>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center gap-4">
        <div className="relative w-12 h-12 shrink-0">
          <img src={track.cover_url} alt="" className={`w-12 h-12 object-cover rounded-full ${!paused && !done ? "spin-vinyl" : ""}`} />
          <Disc3 size={14} className="absolute inset-0 m-auto text-black/70" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-bold text-white uppercase truncate">{track.title}</p>
          <p className="font-mono-tech text-[10px] tracking-[0.2em] text-slate-400 uppercase truncate">
            {track.artist} — {done ? "Preview ended · continue on your platform" : "60s preview"}
          </p>
        </div>
        <div className="hidden sm:flex items-end gap-[3px] h-6" aria-hidden="true">
          {[0.2, 0.5, 0.35, 0.7, 0.45, 0.6, 0.3].map((d, i) => (
            <span key={i} className={`w-1 h-full bg-[#00f0ff] ${!paused && !done ? "eq-bar" : "scale-y-[0.2] origin-bottom"}`} style={{ animationDelay: `${d}s` }} />
          ))}
        </div>
        <span className="font-mono-tech text-xs text-[#00f0ff] tabular-nums">{fmt(elapsed)} / 1:00</span>
        <button
          data-testid="sticky-play-toggle"
          onClick={() => (done ? (setElapsed(0), setPaused(false)) : setPaused(!paused))}
          className="w-10 h-10 flex items-center justify-center bg-[#00f0ff] text-black hover:bg-white transition-colors"
          aria-label={paused || done ? "Play" : "Pause"}
        >
          {paused || done ? <Play size={16} fill="currentColor" className="ml-0.5" /> : <Pause size={16} fill="currentColor" />}
        </button>
        <a
          data-testid="sticky-spotify-link"
          href={track.spotify_url}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden md:inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-wider uppercase px-3 py-2.5 border border-[#00ff66]/40 text-[#00ff66] hover:bg-[#00ff66] hover:text-black transition-colors"
        >
          Full Track <ExternalLink size={10} />
        </a>
        <button data-testid="sticky-player-close" onClick={onClose} aria-label="Close player" className="text-slate-500 hover:text-white transition-colors">
          <X size={18} />
        </button>
      </div>
    </div>
  );
};
